import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { hueClass, hueFor } from '@/lib/hue';
import { useContextSwitch } from '@/app/use-context-switch';
import { roleLabel } from '@/auth/abilities';
import { useSession } from '@/auth/session-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Spinner } from '@/components/ui/Spinner';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';

function TenantSwitcher() {
  const { session, tenant, environment } = useSession();
  const { switchTo, switching } = useContextSwitch();
  const memberships = session.memberships;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-7 max-w-[260px] items-center gap-2 rounded-md px-2 text-base font-medium text-fg hover:bg-hover data-[state=open]:bg-hover"
          aria-label={`Tenant: ${tenant.name}. Switch tenant`}
        >
          <span
            className={cn(
              'grid size-[1.375rem] shrink-0 place-items-center rounded-sm text-2xs leading-none font-semibold tinted',
              hueClass(hueFor(tenant.name)),
            )}
          >
            {tenant.name.charAt(0).toUpperCase()}
          </span>
          <span className="truncate">{tenant.name}</span>
          <ChevronsUpDown aria-hidden className="size-3.5 shrink-0 text-fg-subtle" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Tenants</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.tenant_id}
            disabled={switching}
            onSelect={() => {
              if (m.tenant_id !== tenant.id) void switchTo({ tenantId: m.tenant_id, environment });
            }}
            className="h-auto py-1.5"
          >
            <Building2 aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{m.tenant_name}</span>
              <span className="truncate text-xs text-fg-subtle">
                {m.tenant_slug} · {roleLabel(m.role)}
              </span>
            </span>
            {m.tenant_id === tenant.id && <Check aria-hidden className="!size-3 !text-fg" strokeWidth={2} />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-fg-subtle">
          {tenant.default_currency} · {tenant.time_zone} · {tenant.country}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Test/live switch. Test is amber, live is green, and each carries its word. */
export function EnvironmentToggle() {
  const { environment } = useSession();
  const { switchTo, switching } = useContextSwitch();

  return (
    <div
      role="radiogroup"
      aria-label="Data environment"
      className="relative inline-flex h-7 items-center gap-0.5 rounded-md border border-border-strong p-0.5"
    >
      {(['test', 'live'] as const).map((env) => {
        const active = env === environment;
        return (
          <button
            key={env}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={switching}
            onClick={() => {
              if (!active) void switchTo({ environment: env });
            }}
            className={cn(
              'inline-flex h-full min-w-11 items-center justify-center gap-1.5 rounded-sm px-2 text-xs font-medium tracking-[0.06em] uppercase transition-[background-color,color] duration-[120ms]',
              active ? 'bg-active text-fg' : 'text-fg-muted hover:text-fg',
              active && hueClass(env === 'live' ? 'ok' : 'warn'),
            )}
          >
            {switching && !active ? (
              <Spinner className="size-3" />
            ) : (
              <>
                {active && <span aria-hidden className="size-1.5 rounded-full bg-[var(--h)] dot-halo" />}
                {env}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The bar over the page: whose data this is (tenant, environment). The
 * account lives at the foot of the sidebar; navigation and search in it.
 */
export function Topbar() {
  return (
    <header className="flex h-[var(--topbar-h)] shrink-0 items-center gap-2 border-b border-border pr-4 pl-2">
      <TenantSwitcher />
      <div className="flex-1" />
      <Tooltip content="Switch between test and live data">
        <div>
          <EnvironmentToggle />
        </div>
      </Tooltip>
    </header>
  );
}
